import { I18n } from 'react-redux-i18n';
import React from 'react';
import styled from 'styled-components';
import { SectionInfo } from '../../components/SectionInfo';
import { SearchInput } from '../../components/SearchInput';
import { ColumnDef } from '../../components/ColumnDisplayModal';
import { Pagination } from '../../components/Pagination';
import { PosFilterState } from '../../components/posFilterTypes';
import type { V2DistributionRawRow } from 'apiTyped/hooks/v2.distribution.query';
import type { SimpleSelectOption } from '../../components/SimpleSelect';
import {
  CardDesc,
  CardHead,
  CardHeadActions,
  CardTitle,
  CardTitleRow,
  ColDisplayBadge,
  ColDisplayTrigger,
  EmptyChartState,
  ExportButton,
  GV_ROW_LIMIT_OPTIONS,
  GroupByBtn,
  PosNameCell,
  PosSubtitle,
  RowLimitBar,
  SortableTh,
  Table,
  TableWrap,
  Td,
  TdNum,
  Tr,
  formatRawWineFormat
} from './shared';
import { TableSkeleton } from './loading';
import type { RowLimit, TableSort } from './types';

export function formatRawPrice(
  value?: number | null,
  currency = 'USD'
): string {
  if (value == null) return '—';
  const normalized = Number(value);
  const formatted = Number.isInteger(normalized)
    ? normalized.toFixed(0)
    : normalized.toFixed(2);
  return `${formatted} ${currency}`;
}

export const getRawColumns = (): ColumnDef[] => [
  {
    id: 'date',
    label: I18n.t('v2GlobalView.rawData.columns.date'),
    defaultOn: true
  },
  {
    id: 'market',
    label: I18n.t('v2GlobalView.rawData.columns.market'),
    defaultOn: true
  },
  {
    id: 'pos',
    label: I18n.t('v2GlobalView.rawData.columns.pointOfSale'),
    defaultOn: true
  },
  {
    id: 'wine',
    label: I18n.t('v2GlobalView.rawData.columns.wine'),
    defaultOn: true,
    always: true
  },
  {
    id: 'vintage',
    label: I18n.t('v2GlobalView.rawData.columns.vintage'),
    defaultOn: true
  },
  {
    id: 'format',
    label: I18n.t('v2GlobalView.rawData.columns.format'),
    defaultOn: true
  },
  {
    id: 'price',
    label: I18n.t('v2GlobalView.rawData.columns.price'),
    defaultOn: true
  },
  {
    id: 'evol',
    label: I18n.t('v2GlobalView.rawData.columns.evolution'),
    defaultOn: true
  }
];

export const RawFormatGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border: 1px solid var(--line-soft, #e8e5df);
  border-radius: 999px;
  background: var(--paper, #f9f7f4);
`;

export const RawFormatLabel = styled.span`
  padding: 0 7px;
  color: var(--ink-faint);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
`;

export const TableHint = styled.div`
  color: var(--ink-faint);
  font-size: 12px;
  margin-bottom: 8px;
`;

export interface RawDataTabProps {
  gvFilters: PosFilterState;
  isFetchingRaw: boolean;
  isLoadingRaw: boolean;
  limitedRawRows: V2DistributionRawRow[];
  page: number;
  paginatedRawRows: V2DistributionRawRow[];
  rawColVisibility: Record<string, boolean>;
  rawFormatOptions: SimpleSelectOption[];
  rawMarketLabel: string | null;
  rawRowLimit: RowLimit;
  rawRows: V2DistributionRawRow[];
  rawSort: TableSort<V2DistributionRawRow>;
  rawTotalPages: number;
  search: string;
  setGvFilters: React.Dispatch<React.SetStateAction<PosFilterState>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  isExporting: boolean;
  onExport: () => void;
  setRawColModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setRawRowLimit: React.Dispatch<React.SetStateAction<RowLimit>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
}

export const RawDataTab: React.FC<RawDataTabProps> = ({
  gvFilters,
  isFetchingRaw,
  isLoadingRaw,
  limitedRawRows,
  page,
  paginatedRawRows,
  rawColVisibility,
  rawFormatOptions,
  rawMarketLabel,
  rawRowLimit,
  rawRows,
  rawSort,
  rawTotalPages,
  search,
  setGvFilters,
  setPage,
  isExporting,
  onExport,
  setRawColModalOpen,
  setRawRowLimit,
  setSearch
}) => (
  <TableWrap>
    {isFetchingRaw && !isLoadingRaw && (
      <TableHint>{I18n.t('v2GlobalView.rawData.loading')}</TableHint>
    )}
    <CardHead>
      <div>
        <CardTitleRow>
          <CardTitle>
            {I18n.t('v2GlobalView.rawData.title')}
            <SectionInfo
              title={I18n.t('v2GlobalView.rawData.info.title')}
              body={
                <>
                  <p>{I18n.t('v2GlobalView.rawData.info.body1')}</p>
                  <p>
                    {I18n.t('v2GlobalView.rawData.info.body2Pre')}{' '}
                    <strong>
                      {I18n.t('v2GlobalView.rawData.info.body2BrandProduct')}
                    </strong>
                    {I18n.t('v2GlobalView.rawData.info.body2Mid')}
                    <strong>
                      {I18n.t('v2GlobalView.rawData.info.body2Competitors')}
                    </strong>{' '}
                    {I18n.t('v2GlobalView.rawData.info.body2Suffix')}
                  </p>
                </>
              }
            />
          </CardTitle>
        </CardTitleRow>
        <CardDesc>
          {limitedRawRows.length < rawRows.length
            ? I18n.t('v2GlobalView.rawData.recordsOfTotal', {
                shown: limitedRawRows.length.toLocaleString(),
                total: rawRows.length.toLocaleString()
              })
            : I18n.t(
                rawRows.length === 1
                  ? 'v2GlobalView.rawData.recordsCount_one'
                  : 'v2GlobalView.rawData.recordsCount_other',
                { count: rawRows.length.toLocaleString() }
              )}
          {rawMarketLabel ? ` · ${rawMarketLabel}` : ''}
        </CardDesc>
      </div>
      <CardHeadActions>
        <RawFormatGroup
          role="radiogroup"
          aria-label={I18n.t('v2GlobalView.rawData.formatFilter')}
        >
          <RawFormatLabel>
            {I18n.t('v2GlobalView.rawData.formatFilter')}
          </RawFormatLabel>
          {rawFormatOptions.map((format) => (
            <GroupByBtn
              key={format.value}
              type="button"
              role="radio"
              aria-checked={gvFilters.wineFormatGroup === format.value}
              $active={gvFilters.wineFormatGroup === format.value}
              onClick={() =>
                setGvFilters((previous) => ({
                  ...previous,
                  wineFormatGroup: String(format.value)
                }))
              }
            >
              {format.label}
            </GroupByBtn>
          ))}
        </RawFormatGroup>
        <SearchInput
          placeholder={I18n.t('v2GlobalView.rawData.searchPlaceholder')}
          value={search}
          onChange={setSearch}
        />
        <ColDisplayTrigger
          type="button"
          onClick={() => setRawColModalOpen(true)}
        >
          {I18n.t('v2GlobalView.rawData.columnDisplay')}{' '}
          <ColDisplayBadge>
            {Object.values(rawColVisibility).filter(Boolean).length}
          </ColDisplayBadge>
        </ColDisplayTrigger>
        <ExportButton
          onClick={onExport}
          disabled={isExporting || rawRows.length === 0}
        />
      </CardHeadActions>
    </CardHead>
    {isLoadingRaw ? (
      <TableSkeleton
        label={I18n.t('v2GlobalView.rawData.loading')}
        columns={Math.max(
          Object.values(rawColVisibility).filter(Boolean).length,
          6
        )}
      />
    ) : rawRows.length === 0 ? (
      <EmptyChartState>{I18n.t('v2GlobalView.rawData.noData')}</EmptyChartState>
    ) : (
      <Table>
        <thead>
          <tr>
            {rawColVisibility.date && (
              <SortableTh
                label={I18n.t('v2GlobalView.rawData.columns.date')}
                sortKey="date"
                sort={rawSort.sort}
                onSort={rawSort.onSort}
              />
            )}
            {rawColVisibility.market && (
              <SortableTh
                label={I18n.t('v2GlobalView.rawData.columns.market')}
                sortKey="market"
                sort={rawSort.sort}
                onSort={rawSort.onSort}
              />
            )}
            {rawColVisibility.pos && (
              <SortableTh
                label={I18n.t('v2GlobalView.rawData.columns.pointOfSale')}
                sortKey="pos"
                sort={rawSort.sort}
                onSort={rawSort.onSort}
              />
            )}
            <SortableTh
              label={I18n.t('v2GlobalView.rawData.columns.wine')}
              sortKey="wine"
              sort={rawSort.sort}
              onSort={rawSort.onSort}
            />
            {rawColVisibility.vintage && (
              <SortableTh
                label={I18n.t('v2GlobalView.rawData.columns.vintage')}
                sortKey="vintage"
                sort={rawSort.sort}
                onSort={rawSort.onSort}
              />
            )}
            {rawColVisibility.format && (
              <SortableTh
                label={I18n.t('v2GlobalView.rawData.columns.format')}
                sortKey="format"
                sort={rawSort.sort}
                onSort={rawSort.onSort}
              />
            )}
            {rawColVisibility.price && (
              <SortableTh
                label={I18n.t('v2GlobalView.rawData.columns.price')}
                sortKey="price"
                sort={rawSort.sort}
                onSort={rawSort.onSort}
                align="right"
              />
            )}
            {rawColVisibility.evol && (
              <SortableTh
                label={I18n.t('v2GlobalView.rawData.columns.evolution')}
                sortKey="evol"
                sort={rawSort.sort}
                onSort={rawSort.onSort}
              />
            )}
          </tr>
        </thead>
        <tbody>
          {paginatedRawRows.map((row, i) => (
            <Tr key={`${row.store_id ?? 'store'}-${row.wine_id ?? i}-${i}`}>
              {rawColVisibility.date && (
                <Td>
                  {row.survey_year === '9999' ||
                  row.survey_year === '9998' ||
                  row.survey_year === '9990' ||
                  !row.survey_year
                    ? I18n.t('v2GlobalView.rawData.currentSurvey')
                    : row.survey_year}
                </Td>
              )}
              {rawColVisibility.market && <Td>{row.market_name || '—'}</Td>}
              {rawColVisibility.pos && (
                <Td>
                  <PosNameCell>
                    <strong>{row.store_name || '—'}</strong>
                    {row.market_name && (
                      <PosSubtitle>{row.market_name}</PosSubtitle>
                    )}
                  </PosNameCell>
                </Td>
              )}
              <Td>
                <PosNameCell>
                  <strong>{row.wine_name || '—'}</strong>
                  {row.marque_name && (
                    <PosSubtitle>{row.marque_name}</PosSubtitle>
                  )}
                </PosNameCell>
              </Td>
              {rawColVisibility.vintage && <Td>{row.vintage || '—'}</Td>}
              {rawColVisibility.format && (
                <Td>{formatRawWineFormat(row.wine_format_group_id)}</Td>
              )}
              {rawColVisibility.price && (
                <TdNum>
                  {formatRawPrice(row.price, row.displayed_currency)}
                </TdNum>
              )}
              {rawColVisibility.evol && <Td>{row.evolution_last || '—'}</Td>}
            </Tr>
          ))}
        </tbody>
      </Table>
    )}
    <RowLimitBar>
      {GV_ROW_LIMIT_OPTIONS.map((limit) => (
        <GroupByBtn
          key={limit}
          type="button"
          $active={rawRowLimit === limit}
          onClick={() => {
            setRawRowLimit(limit);
            setPage(1);
          }}
        >
          {I18n.t('v2GlobalView.marketOverview.showRows', {
            count: limit
          })}
        </GroupByBtn>
      ))}
    </RowLimitBar>
    <Pagination
      page={page}
      totalPages={rawTotalPages}
      onPrevious={() => setPage((p) => Math.max(1, p - 1))}
      onNext={() => setPage((p) => Math.min(rawTotalPages, p + 1))}
    />
  </TableWrap>
);
